import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { fetchAllUsers } from '../store/slices/authSlice'
import {
  getBookingsByCustomer,
  getInstallmentsByBooking,
  applyConcession
} from '../api'
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext'
import api from '../api'
import Swal from 'sweetalert2'

const ConcessionForm = ({ onConcessionUpdate }) => {
  const dispatch = useDispatch()
  const { user, users } = useSelector(state => state.auth)
  const { activeSociety } = useSidebarContext()
  const [loading, setLoading] = useState(false)
  const [customers, setCustomers] = useState([])
  const [filteredCustomers, setFilteredCustomers] = useState([])
  const [customersLoading, setCustomersLoading] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState('')
  const [customerBookings, setCustomerBookings] = useState([])
  const [selectedBooking, setSelectedBooking] = useState('')
  const [selectedPlot, setSelectedPlot] = useState('')
  const [concessionType, setConcessionType] = useState('percentage')
  const [concessionValue, setConcessionValue] = useState('')
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')
  const [calculatedConcession, setCalculatedConcession] = useState(0)
  const [newTotalAmount, setNewTotalAmount] = useState(0)
  const [errors, setErrors] = useState({})
  const [installments, setInstallments] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [installmentStats, setInstallmentStats] = useState({
    totalInstallments: 0,
    paidInstallments: 0,
    pendingInstallments: 0,
    totalPaidAmount: 0,
    totalPendingAmount: 0,
    totalOriginalAmount: 0
  })

  const dropdownRef = useRef(null)
  const inputRef = useRef(null)

  // Fetch customers on component mount
  useEffect(() => {
    fetchCustomers()
  }, [])

  // Fetch customer bookings when customer changes
  useEffect(() => {
    if (selectedCustomer) {
      fetchCustomerBookings(selectedCustomer)
    }
  }, [selectedCustomer])

  // Fetch installments when booking changes
  useEffect(() => {
    if (selectedBooking) {
      fetchInstallments(selectedBooking)
    }
  }, [selectedBooking])

  // Calculate concession when values change
  useEffect(() => {
    if (selectedBooking && concessionValue && concessionType) {
      calculateConcession()
    }
  }, [selectedBooking, concessionValue, concessionType])

  // Filter customers when users data changes
  useEffect(() => {
    // console.log('Users data changed:', users)
    // console.log('Users array length:', users?.length)
    // console.log('Users array:', users)
    // console.log('Active Society:', activeSociety?.name, activeSociety?._id)

    if (users && users.length > 0 && activeSociety) {
      // Filter only active customers who belong to the active society
      const customerUsers = users.filter(
        user =>
          (user.role === 'Customer' || user.role === 'customer') &&
          user.status === 'active' &&
          user.societies &&
          user.societies.some(society => society._id === activeSociety._id)
      )
      // console.log('Filtered active society customers:', customerUsers)
      // console.log('Active society customers length:', customerUsers.length)
      // console.log(
      //   'Active society customers details:',
      //   customerUsers.map(c => ({
      //     id: c._id,
      //     name: c.customerData?.name || c.name || c.username,
      //     username: c.username,
      //     role: c.role,
      //     status: c.status,
      //     societies: c.societies,
      //     customerData: c.customerData
      //   }))
      // )

      // Also log all roles to see what's available
      const allRoles = [...new Set(users.map(u => u.role))]
      // console.log('All available roles:', allRoles)

      // Debug plot data for customers
      customerUsers.forEach(customer => {
        if (customer.customerData?.plots) {
          // console.log(
          //   `Customer ${
          //     customer.customerData.name || customer.username
          //   } plots:`,
          //   {
          //     plotsArray: customer.customerData.plots,
          //     plotsLength: customer.customerData.plots.length,
          //     plotNumbers: customer.customerData.plots.map(p => p?.plot_number),
          //     plotObjects: customer.customerData.plots
          //   }
          // )
        }
      })

      setCustomers(customerUsers)
      setFilteredCustomers(customerUsers)
    } else {
      // console.log('No users data available or no active society')
      setCustomers([])
      setFilteredCustomers([])
    }
  }, [users, activeSociety])

  // Filter customers based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCustomers(customers)
    } else {
      const filtered = customers.filter(customer => {
        const customerName =
          customer.customerData?.name ||
          customer.name ||
          customer.username ||
          ''
        const customerPhone =
          customer.customerData?.contactInfo?.phone ||
          customer.contactInfo?.phone ||
          customer.phone ||
          ''

        return (
          customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customerPhone.includes(searchTerm)
        )
      })
      setFilteredCustomers(filtered)
    }
  }, [searchTerm, customers])

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false)
        setSelectedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Keyboard navigation
  const handleKeyDown = e => {
    if (!showDropdown) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev =>
          prev < filteredCustomers.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : filteredCustomers.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && filteredCustomers[selectedIndex]) {
          handleCustomerChange(filteredCustomers[selectedIndex])
        }
        break
      case 'Escape':
        setShowDropdown(false)
        setSelectedIndex(-1)
        break
      default:
        break
    }
  }

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && showDropdown) {
      const dropdownElement = dropdownRef.current
      if (dropdownElement) {
        const selectedElement = dropdownElement.querySelector(
          `[data-index="${selectedIndex}"]`
        )
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest' })
        }
      }
    }
  }, [selectedIndex, showDropdown])

  const fetchCustomers = async () => {
    try {
      setCustomersLoading(true)
      // console.log('Fetching customers...')
      // Use Redux to fetch all users (including customers)
      const result = await dispatch(fetchAllUsers())
      // console.log('fetchAllUsers result:', result)
      // console.log('Customers fetched successfully')

      // Also test direct API call
      try {
        const directResponse = await api.get('/users')
        // console.log('Direct API response:', directResponse.data)
        // console.log('Direct API users count:', directResponse.data.length)
        // console.log('Direct API users roles:', [
        //   ...new Set(directResponse.data.map(u => u.role))
        // ])
      } catch (directError) {
        console.error('Direct API call failed:', directError)
      }
    } catch (error) {
      console.error('Error fetching customers:', error)
    } finally {
      setCustomersLoading(false)
    }
  }

  const fetchCustomerBookings = async customerId => {
    try {
      // console.log('Fetching bookings for customer:', customerId._id)
      const response = await getBookingsByCustomer(customerId._id)
      if (response.success) {
        // console.log('Bookings fetched:', response.data)
        setCustomerBookings(response.data)
      } else {
        console.error('Failed to fetch bookings:', response.message)
      }
    } catch (error) {
      console.error('Error fetching customer bookings:', error)
    }
  }

  const fetchInstallments = async bookingId => {
    try {
      // console.log('Fetching installments for booking:', bookingId)
      const response = await getInstallmentsByBooking(bookingId)
      if (response.success) {
        const installmentsData = response.data
        // console.log('Installments fetched:', installmentsData)
        setInstallments(installmentsData)

        // Calculate installment statistics
        const stats = calculateInstallmentStats(installmentsData)
        setInstallmentStats(stats)
      } else {
        console.error('Failed to fetch installments:', response.message)
      }
    } catch (error) {
      console.error('Error fetching installments:', error)
    }
  }

  const calculateInstallmentStats = installmentsData => {
    // console.log('Calculating stats for installments:', installmentsData)

    // Log all unique status values to see what we're working with
    const uniqueStatuses = [
      ...new Set(installmentsData.map(inst => inst.status))
    ]
    // console.log('Unique status values found:', uniqueStatuses)

    const totalInstallments = installmentsData.length

    // Check for completed/paid status values based on Installment model
    const paidInstallments = installmentsData.filter(
      inst => inst.status === 'Completed' || inst.status === 'completed'
    ).length

    const pendingInstallments = totalInstallments - paidInstallments

    // Calculate amounts based on status
    const totalPaidAmount = installmentsData
      .filter(
        inst => inst.status === 'Completed' || inst.status === 'completed'
      )
      .reduce((sum, inst) => sum + (inst.paid_amount || inst.amount || 0), 0)

    const totalPendingAmount = installmentsData
      .filter(
        inst => !(inst.status === 'Completed' || inst.status === 'completed')
      )
      .reduce((sum, inst) => sum + (inst.amount || 0), 0)

    const totalOriginalAmount = installmentsData.reduce(
      (sum, inst) => sum + (inst.original_amount || inst.amount || 0),
      0
    )

    const stats = {
      totalInstallments,
      paidInstallments,
      pendingInstallments,
      totalPaidAmount,
      totalPendingAmount,
      totalOriginalAmount
    }

    // console.log('Calculated stats:', stats)
    return stats
  }

  const calculateConcession = () => {
    const booking = customerBookings.find(b => b._id === selectedBooking)
    if (!booking || !concessionValue) return

    let concession = 0
    let newTotal = 0

    if (concessionType === 'percentage') {
      concession = (booking.total_amount * parseFloat(concessionValue)) / 100
      newTotal = booking.total_amount - concession
    } else if (concessionType === 'fixed_amount') {
      concession = parseFloat(concessionValue)
      newTotal = booking.total_amount - concession
    } else if (concessionType === 'total_amount') {
      newTotal = parseFloat(concessionValue)
      concession = booking.total_amount - newTotal
    }

    setCalculatedConcession(concession)
    setNewTotalAmount(newTotal)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!selectedCustomer) newErrors.customer_id = 'Customer is required'
    if (!selectedBooking) newErrors.booking = 'Booking is required'
    if (!concessionValue)
      newErrors.concessionValue = 'Concession value is required'
    if (!reason) newErrors.reason = 'Reason is required'

    if (
      concessionType === 'percentage' &&
      (concessionValue <= 0 || concessionValue > 100)
    ) {
      newErrors.concessionValue = 'Percentage must be between 1 and 100'
    }

    if (concessionType === 'fixed_amount' && concessionValue <= 0) {
      newErrors.concessionValue = 'Fixed amount must be greater than 0'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!validateForm()) return

    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Confirm Concession Application',
      html: `
        <div class="text-left">
          <p><strong>Concession Type:</strong> PKR{
            concessionType === 'percentage'
              ? 'Percentage'
              : concessionType === 'fixed_amount'
              ? 'Fixed Amount'
              : 'New Total Amount'
          }</p>
          <p><strong>Concession Value:</strong> PKR{
            concessionType === 'percentage'
              ? concessionValue + '%'
              : 'PKR' + concessionValue
          }</p>
          <p><strong>Concession Amount:</strong> PKR${calculatedConcession.toLocaleString()}</p>
          <p><strong>New Total Amount:</strong> PKR${newTotalAmount.toLocaleString()}</p>
          <p><strong>Reason:</strong> ${reason}</p>
          <p class="text-yellow-400"><strong>Note:</strong> This will update all pending installments proportionally.</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Apply Concession',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'swal2-dark',
        title: 'text-white',
        htmlContainer: 'text-gray-300'
      }
    })

    if (!result.isConfirmed) {
      return
    }

    setLoading(true)
    try {
      const booking = customerBookings.find(b => b._id === selectedBooking)

      const concessionData = {
        customer_id: selectedCustomer,
        booking_id: selectedBooking,
        plot_id: booking.plot_id,
        concession_type: concessionType,
        concession_value: parseFloat(concessionValue),
        reason,
        notes
      }

      // console.log('About to call applyConcession with data:', concessionData)
      // console.log('applyConcession function:', applyConcession)

      const response = await applyConcession(concessionData)

      // console.log('applyConcession response:', response)

      if (response.success) {
        Swal.fire({
          title: 'Concession Applied Successfully!',
          html: `
            <div class="text-left">
              <p><strong>Concession Amount:</strong> $PKR{
                response.data.concession_amount?.toLocaleString() || 'N/A'
              }</p>
              <p><strong>New Total Amount:</strong> PKR${
                response.data.new_total_amount?.toLocaleString() || 'N/A'
              }</p>
              <p><strong>Installments Updated:</strong> ${
                response.data.updated_installments || 0
              }</p>
              <p><strong>Reason:</strong> ${reason}</p>
            </div>
          `,
          icon: 'success',
          confirmButtonColor: '#10b981',
          confirmButtonText: 'OK',
          customClass: {
            popup: 'swal2-dark',
            title: 'text-white',
            htmlContainer: 'text-gray-300'
          }
        })
        // Reset form
        setSelectedCustomer('')
        setSelectedBooking('')
        setSelectedPlot('')
        setConcessionType('percentage')
        setConcessionValue('')
        setReason('')
        setNotes('')
        setCalculatedConcession(0)
        setNewTotalAmount(0)
        setErrors({})
        setInstallments([])
        setInstallmentStats({
          totalInstallments: 0,
          paidInstallments: 0,
          pendingInstallments: 0,
          totalPaidAmount: 0,
          totalPendingAmount: 0,
          totalOriginalAmount: 0
        })

        if (onConcessionUpdate) {
          onConcessionUpdate()
        }
      }
    } catch (error) {
      console.error('Error applying concession:', error)
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      })
      Swal.fire({
        title: 'Error Applying Concession',
        html: `
          <div class="text-left">
            <p><strong>Error:</strong> ${
              error.response?.data?.message || 'Unknown error occurred'
            }</p>
            ${
              error.response?.data?.error
                ? `<p><strong>Details:</strong> ${error.response.data.error}</p>`
                : ''
            }
            <p><strong>Status:</strong> ${
              error.response?.status || 'Unknown'
            }</p>
          </div>
        `,
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'swal2-dark',
          title: 'text-white',
          htmlContainer: 'text-gray-300'
        }
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCustomerChange = customer => {
    setSelectedCustomer(customer)
    setSearchTerm('') // Clear search term
    setShowDropdown(false) // Hide dropdown
    setSelectedIndex(-1) // Reset selected index
    setCustomerBookings([])
    setSelectedBooking('')
    setInstallments([])
  }

  const handleBookingChange = bookingId => {
    setSelectedBooking(bookingId)
    setSelectedPlot('')
    setInstallments([])
  }

  const clearForm = () => {
    setSelectedCustomer('')
    setSearchTerm('')
    setShowDropdown(false)
    setSelectedIndex(-1)
    setCustomerBookings([])
    setSelectedBooking('')
    setInstallments([])
    setConcessionType('percentage')
    setConcessionValue('')
    setReason('')
    setNotes('')
    setCalculatedConcession(0)
    setNewTotalAmount(0)
    setErrors({})
    setInstallmentStats({
      totalInstallments: 0,
      paidInstallments: 0,
      pendingInstallments: 0,
      totalPaidAmount: 0,
      totalPendingAmount: 0,
      totalOriginalAmount: 0
    })
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
    setShowDropdown(true) // Keep dropdown open while typing
  }

  return (
    <div className='p-6'>
      <div className='mb-6'>
        <h2 className='text-xl font-semibold text-white mb-2'>
          Apply for Customer Concession
        </h2>
        <p className='text-gray-300'>
          Fill out the form below to apply for a customer concession. The system
          will automatically calculate the impact on remaining installments.
        </p>

        {/* Debug section */}
        <div className='mt-4 p-3 bg-slate-800/60 border border-slate-700/50 rounded-md'>
          {/* <p className='text-sm text-teal-400 mb-2'>Debug Info:</p>
          <p className='text-xs text-teal-300/70'>
            Active Society: {activeSociety?.name || 'None'}
          </p>
          <p className='text-xs text-teal-300/70'>
            Society ID: {activeSociety?._id || 'None'}
          </p>
          <p className='text-xs text-teal-300/70'>
            Users in Redux: {users?.length || 0}
          </p>
          <p className='text-xs text-teal-300/70'>
            Active Society Customers: {customers?.length || 0}
          </p>
          <p className='text-xs text-teal-300/70'>
            Filtered Customers: {filteredCustomers?.length || 0}
          </p>
          <p className='text-xs text-teal-300/70'>
            Customers Loading: {customersLoading ? 'Yes' : 'No'}
          </p>
          <p className='text-xs text-teal-300/70'>
            Current user role: {user?.role || 'Unknown'}
          </p>
          <p className='text-xs text-teal-300/70'>
            Selected Customer: {selectedCustomer || 'None'}
          </p>
          <p className='text-xs text-teal-300/70'>
            Customer Bookings: {customerBookings?.length || 0}
          </p>
          <p className='text-xs text-teal-300/70'>
            Selected Booking: {selectedBooking || 'None'}
          </p>
          <p className='text-xs text-teal-300/70'>
            Selected Plot: {selectedPlot || 'None'}
          </p>
          <p className='text-xs text-teal-300/70'>
            Concession Type: {concessionType}
          </p>
          <p className='text-xs text-teal-300/70'>
            Concession Value: {concessionValue}
          </p> */}

          <div className='mt-2 flex gap-2'>
            <button
              onClick={fetchCustomers}
              className='px-3 py-1 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-xs rounded hover:from-teal-600 hover:to-cyan-600 transition-all duration-200'
            >
              Refresh Customers
            </button>
            <button
              onClick={clearForm}
              className='px-3 py-1 bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs rounded hover:from-slate-700 hover:to-slate-800 transition-all duration-200'
            >
              Clear Form
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className='space-y-6'>
        {/* Customer Selection */}
        <div>
          <label className='block text-sm font-medium text-teal-300/70 mb-2'>
            Select Customer *
          </label>
          <div className='relative'>
            <div className='relative'>
              <input
                type='text'
                value={
                  selectedCustomer
                    ? `${
                        selectedCustomer.customerData?.name ||
                        selectedCustomer.name ||
                        selectedCustomer.username
                      } - ${
                        selectedCustomer.customerData?.contactInfo?.phone ||
                        'No Phone'
                      }`
                    : searchTerm
                }
                onChange={handleSearchChange}
                onFocus={() => setShowDropdown(true)}
                onKeyDown={handleKeyDown}
                placeholder='Search customers by name or phone number...'
                className='w-full px-4 py-3 border border-slate-700/50 bg-slate-800/60 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200'
              />
              <div className='absolute inset-y-0 right-0 flex items-center pr-3'>
                <svg
                  className='h-5 w-5 text-teal-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
              </div>
              {selectedCustomer && (
                <button
                  onClick={() => handleCustomerChange('')}
                  className='absolute inset-y-0 right-8 flex items-center pr-2 text-teal-400 hover:text-teal-300 transition-colors duration-150'
                >
                  <svg
                    className='h-4 w-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Dropdown */}
            {showDropdown && (
              <div className='absolute z-10 w-full mt-1 bg-slate-900/90 border border-slate-700/50 rounded-lg shadow-lg max-h-60 overflow-auto'>
                {/* Dropdown Header */}
                <div className='px-3 py-2 bg-slate-800/60 border-b border-slate-700/30'>
                  <p className='text-xs text-teal-300/70'>
                    {customersLoading
                      ? 'Loading customers...'
                      : `${filteredCustomers.length} active customer${
                          filteredCustomers.length !== 1 ? 's' : ''
                        } from ${
                          activeSociety?.name || 'selected society'
                        } found`}
                  </p>
                </div>

                {/* Customer List */}
                {customersLoading ? (
                  <div className='px-3 py-4 text-center'>
                    <div className='inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500'></div>
                    <p className='mt-2 text-sm text-teal-300/70'>
                      Loading customers...
                    </p>
                  </div>
                ) : filteredCustomers.length === 0 ? (
                  <div className='px-3 py-2 text-teal-300/70 text-sm'>
                    {searchTerm.trim() === ''
                      ? `No active customers found in ${
                          activeSociety?.name || 'selected society'
                        }`
                      : `No customers found matching "${searchTerm}"`}
                  </div>
                ) : (
                  filteredCustomers.map((customer, index) => (
                    <div
                      key={customer._id}
                      onClick={() => handleCustomerChange(customer)}
                      className={`px-3 py-2 cursor-pointer transition-colors duration-150 ${
                        index === selectedIndex
                          ? 'bg-teal-500/20 text-teal-300'
                          : 'hover:bg-slate-700/50 text-white'
                      }`}
                      data-index={index}
                    >
                      <div className='font-medium'>
                        {customer.customerData?.name ||
                          customer.name ||
                          customer.username}
                      </div>
                      <div className='text-sm text-teal-300/70'>
                        {customer.customerData?.contactInfo?.phone ||
                          customer.phone ||
                          'No phone'}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Help Text */}
            <p className='mt-1 text-xs text-teal-300/50'>
              💡 Type to search customers by name or phone number. Only active
              customers from {activeSociety?.name || 'the selected society'} are
              shown.
            </p>
            <p className='mt-1 text-xs text-teal-300/50'>
              ⌨️ Use ↑↓ arrows to navigate, Enter to select, Esc to close
            </p>
          </div>
          {errors.customer_id && (
            <p className='mt-1 text-sm text-red-400'>{errors.customer_id}</p>
          )}
        </div>

        {/* Booking Selection */}
        <div>
          <label className='block text-sm font-medium text-gray-300 mb-2'>
            Booking *
          </label>
          <select
            value={selectedBooking}
            onChange={e => handleBookingChange(e.target.value)}
            disabled={!selectedCustomer}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              errors.booking
                ? 'border-red-500 bg-gray-700 text-white'
                : 'border-gray-600 bg-gray-700 text-white'
            } ${!selectedCustomer ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <option value=''>Select Booking</option>
            {customerBookings.map(booking => (
              <option key={booking._id} value={booking._id}>
                Booking #{booking.booking_number} - Plot{' '}
                {booking.plot_id?.plot_number || 'N/A'} (Block{' '}
                {booking.plot_id?.block || 'N/A'}) - $
                {booking.total_amount?.toLocaleString()}
              </option>
            ))}
          </select>
          {errors.booking && (
            <p className='mt-1 text-sm text-red-400'>{errors.booking}</p>
          )}
        </div>

        {/* Installment Status Display */}
        {selectedBooking && installments.length > 0 && (
          <div className='bg-gray-700 border border-gray-600 rounded-md p-4'>
            <h3 className='text-sm font-medium text-white mb-3'>
              Current Installment Status
            </h3>

            {/* Plot Information */}
            {customerBookings.find(b => b._id === selectedBooking)?.plot_id && (
              <div className='mb-3 p-2 bg-gray-600 rounded'>
                <p className='text-xs text-gray-300'>
                  <span className='font-medium'>Plot:</span>{' '}
                  {
                    customerBookings.find(b => b._id === selectedBooking)
                      ?.plot_id?.plot_number
                  }
                  <span className='ml-2'>
                    (Block{' '}
                    {
                      customerBookings.find(b => b._id === selectedBooking)
                        ?.plot_id?.block
                    }
                    )
                  </span>
                  <span className='ml-2'>
                    Size:{' '}
                    {
                      customerBookings.find(b => b._id === selectedBooking)
                        ?.plot_id?.size
                    }
                  </span>
                </p>
              </div>
            )}

            <div className='grid grid-cols-2 md:grid-cols-4 gap-4 text-sm'>
              <div>
                <span className='text-gray-400'>Total Installments:</span>
                <span className='ml-2 font-medium text-white'>
                  {installmentStats.totalInstallments}
                </span>
              </div>
              <div>
                <span className='text-gray-400'>Paid:</span>
                <span className='ml-2 font-medium text-emerald-400'>
                  {installmentStats.paidInstallments}
                </span>
              </div>
              <div>
                <span className='text-gray-400'>Pending:</span>
                <span className='ml-2 font-medium text-orange-400'>
                  {installmentStats.pendingInstallments}
                </span>
              </div>
              <div>
                <span className='text-gray-400'>Total Pending Amount:</span>
                <span className='ml-2 font-medium text-orange-400'>
                  ${installmentStats.totalPendingAmount?.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Installment Details */}
            <div className='mt-3'>
              <h4 className='text-xs font-medium text-gray-300 mb-2'>
                Installment Breakdown:
              </h4>
              <div className='space-y-2 max-h-32 overflow-y-auto'>
                {installments.map((installment, index) => {
                  // Check if installment is paid using the same logic as stats calculation
                  const isPaid =
                    installment.status === 'Completed' ||
                    installment.status === 'completed'

                  // Get display text based on actual status
                  const getStatusText = status => {
                    switch (status) {
                      case 'Completed':
                      case 'completed':
                        return 'Paid'
                      case 'Pending':
                      case 'pending':
                        return 'Pending'
                      case 'Partially Paid':
                      case 'partially_paid':
                        return 'Partial'
                      case 'Overdue':
                      case 'overdue':
                        return 'Overdue'
                      default:
                        return status || 'Pending'
                    }
                  }

                  const getStatusColor = status => {
                    switch (status) {
                      case 'Completed':
                      case 'completed':
                        return 'text-emerald-400'
                      case 'Pending':
                      case 'pending':
                        return 'text-orange-400'
                      case 'Partially Paid':
                      case 'partially_paid':
                        return 'text-yellow-400'
                      case 'Overdue':
                      case 'overdue':
                        return 'text-red-400'
                      default:
                        return 'text-orange-400'
                    }
                  }

                  return (
                    <div
                      key={installment._id}
                      className='flex justify-between text-xs'
                    >
                      <span className='text-gray-400'>
                        Installment {index + 1}: $
                        {installment.amount?.toLocaleString()}
                      </span>
                      <span
                        className={`font-medium ${getStatusColor(
                          installment.status
                        )}`}
                      >
                        {getStatusText(installment.status)}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {/* Concession Type */}
        <div>
          <label className='block text-sm font-medium text-gray-300 mb-2'>
            Concession Type *
          </label>
          <select
            value={concessionType}
            onChange={e => setConcessionType(e.target.value)}
            className='w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500'
          >
            <option value='percentage'>Percentage (%)</option>
            <option value='fixed_amount'>Fixed Amount (PKR)</option>
            <option value='total_amount'>New Total Amount (PKR)</option>
          </select>
        </div>

        {/* Concession Value */}
        <div>
          <label className='block text-sm font-medium text-gray-300 mb-2'>
            Concession Value *
          </label>
          <input
            type='number'
            value={concessionValue}
            onChange={e => setConcessionValue(e.target.value)}
            placeholder={
              concessionType === 'percentage'
                ? 'Enter percentage (1-100)'
                : 'Enter amount'
            }
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              errors.concessionValue
                ? 'border-red-500 bg-gray-700 text-white'
                : 'border-gray-600 bg-gray-700 text-white'
            }`}
          />
          {errors.concessionValue && (
            <p className='mt-1 text-sm text-red-400'>
              {errors.concessionValue}
            </p>
          )}
        </div>

        {/* Concession Calculation Preview */}
        {calculatedConcession > 0 && (
          <div className='bg-teal-900/20 border border-teal-700 rounded-md p-4'>
            <h3 className='text-sm font-medium text-teal-300 mb-2'>
              Concession Preview
            </h3>
            <div className='space-y-2 text-sm'>
              <div className='flex justify-between'>
                <span className='text-gray-300'>Original Total:</span>
                <span className='text-white'>
                  PKR
                  {customerBookings
                    .find(b => b._id === selectedBooking)
                    ?.total_amount?.toLocaleString()}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-gray-300'>Concession Amount:</span>
                <span className='text-teal-300'>
                  -PKR{calculatedConcession.toLocaleString()}
                </span>
              </div>
              <div className='flex justify-between font-medium'>
                <span className='text-gray-300'>New Total:</span>
                <span className='text-emerald-400'>
                  PKR{newTotalAmount.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Impact on Pending Installments */}
            {installmentStats.pendingInstallments > 0 && (
              <div className='mt-3 pt-3 border-t border-teal-700'>
                <h4 className='text-sm font-medium text-teal-300 mb-2'>
                  Impact on Pending Installments:
                </h4>
                <div className='text-sm text-teal-200'>
                  <p>
                    • {installmentStats.pendingInstallments} pending
                    installments will be recalculated
                  </p>
                  <p>
                    • Each pending installment will be reduced proportionally
                  </p>
                  <p>
                    • Total pending amount will be reduced from PKR
                    {installmentStats.totalPendingAmount?.toLocaleString()} to
                    approximately PKR
                    {(
                      installmentStats.totalPendingAmount - calculatedConcession
                    ).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reason */}
        <div>
          <label className='block text-sm font-medium text-gray-300 mb-2'>
            Reason for Concession *
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            placeholder='Explain the reason for this concession...'
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              errors.reason
                ? 'border-red-500 bg-gray-700 text-white'
                : 'border-gray-600 bg-gray-700 text-white'
            }`}
          />
          {errors.reason && (
            <p className='mt-1 text-sm text-red-400'>{errors.reason}</p>
          )}
        </div>

        {/* Additional Notes */}
        <div>
          <label className='block text-sm font-medium text-gray-300 mb-2'>
            Additional Notes
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder='Any additional notes or comments...'
            className='w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500'
          />
        </div>

        {/* Submit Button */}
        <div className='flex justify-end'>
          <button
            type='submit'
            disabled={loading}
            className={`px-6 py-2 rounded-md font-medium transition-colors duration-200 ${
              loading
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white hover:from-teal-600 hover:to-emerald-700'
            }`}
          >
            {loading ? 'Applying...' : 'Apply Concession'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default ConcessionForm
